import {inject, injectable} from 'inversify';
import {runInAction} from 'mobx';

import type {BNTenantApiModel} from '../../../../api/types/bnrks/models';
import type {CoreTenantApiModel} from '../../../../api/types/core/models';
import type {Combine} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {TenantModel} from '../Tenant.model';

import {CoreServiceLocator} from '../../../system/serviceLocator/CoreServiceLocator';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {SubtenantFactory} from '../../Subtenant/Subtenant.factory';
import {TenantFactory} from '../Tenant.factory';
import {TenantExpandJrpc} from './Tenant.expand.jrpc';

@injectable()
export class TenantMapperJrpc
  implements
    IApiDataMapper<TenantMapperJrpc.ApiData, TenantModel.Props, TenantModel>
{
  constructor(
    @inject(CoreServiceLocator) private readonly LOCATOR: CoreServiceLocator,
    @inject(TenantFactory)
    private readonly factory: TenantFactory,
    @inject(TenantExpandJrpc)
    public readonly expand: TenantExpandJrpc,
    @inject(SubtenantFactory)
    private readonly subtenantFactory: SubtenantFactory,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
  ) {}

  mapDataToModel(apiData: TenantMapperJrpc.ApiData): TenantModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToModelWithSubtenants(
    apiData: TenantMapperJrpc.ExpandedApiData,
    skipNotFoundSubtenants = false,
  ): TenantModel {
    const props = this.mapDataToProps(apiData);

    const tenant = this.factory.create(props);

    const subtenants = this.processSubtenants(
      apiData,
      tenant,
      skipNotFoundSubtenants,
    );

    runInAction(() => {
      tenant.subtenants = subtenants;
    });

    return tenant;
  }

  mapDataToProps(apiData: TenantMapperJrpc.ApiData): TenantModel.Props {
    return {
      clientId: apiData.client_id,
      created: this.dateTimeModelFactory.createFromDateTimeObject(
        apiData.created,
      ),
      id: apiData._id,
      notificationUrl: apiData.notification_url || null,
      reporting: apiData.sftp_report,
      subtenants: [],
      templateFolder: apiData.template_folder || '',
      title: apiData.title,
      updated: this.dateTimeModelFactory.createFromDateTimeObject(
        apiData.updated,
      ),
    };
  }

  private processSubtenants(
    apiData: TenantMapperJrpc.ExpandedApiData,
    tenant: TenantModel,
    skipNotFound = false,
  ): TenantModel.Props['subtenants'] {
    const mapper = this.LOCATOR.get(SubtenantMapperJrpc);
    const result: TenantModel.Props['subtenants'] = [];

    for (const subtenant of apiData.subtenants) {
      if (subtenant) {
        result.push(mapper.mapDataToModel(subtenant, tenant));
      } else if (!skipNotFound) {
        result.push(this.subtenantFactory.createEmpty());
      }
    }

    return result;
  }
}

export namespace TenantMapperJrpc {
  export type ApiData = Combine<CoreTenantApiModel, BNTenantApiModel>;

  export type ExpandedApiData = ApiData & TenantExpandJrpc.ExpandedFields;
}
